<template>
  <div>
    <Header>
      <template #left><BtnBack /></template>
      <template #title>가이드</template>
    </Header>
    <el-tabs
      v-model="selectedTab"
      class="guide-tabs"
      stretch
    >
      <el-tab-pane
        label="eSIM"
        name="esim"
      >
        <div class="guides">
          <GuidePageAdsBanner v-if="!partnersKey" />
          <div
            v-for="(guide, index) of esimGuides"
            :key="index"
            class="guide"
          >
            <NuxtLink :to="linkTo(guide)">
              <nuxt-img
                :src="url(`/guides/banner/${guide.imgLabel}.webp`)"
                class="w-full guide-banner"
              />
              <div class="texts flex flex-column flex-align-center">
                <h3 class="title">
                  {{ guide.title }}
                </h3>
                <p class="content mb-16">
                  {{ guide.content }}
                </p>
                <div class="button flex">
                  <span>{{ guide.button }}</span>
                  <Icons
                    path="outline/chevron-right"
                    size="16"
                    color="#0176F9"
                  />
                </div>
              </div>
            </NuxtLink>
          </div>
        </div>
      </el-tab-pane>

      <el-tab-pane
        label="USIM"
        name="usim"
      >
        <div class="guides">
          <GuidePageAdsBanner v-if="!partnersKey" />
          <div
            v-for="(guide, index) of usimGuides"
            :key="index"
            class="guide"
          >
            <NuxtLink :to="linkTo(guide)">
              <nuxt-img
                :src="url(`/guides/banner/${guide.imgLabel}.webp`)"
                class="w-full guide-banner"
              />
              <div class="texts flex flex-column flex-align-center">
                <h3 class="title">
                  {{ guide.title }}
                </h3>
                <p class="content mb-16">
                  {{ guide.content }}
                </p>
                <div class="button flex">
                  <span>{{ guide.button }}</span>
                  <Icons
                    path="outline/chevron-right"
                    size="16"
                    color="#0176F9"
                  />
                </div>
              </div>
            </NuxtLink>
          </div>
        </div>
      </el-tab-pane>
    </el-tabs>
  </div>
</template>

<script setup lang="ts">
const { url } = useBlob()
const { partnersKey } = useAuthStore()
const { query } = useRoute()
const type = query.type
const defaultTab =
  useRouter().options.history.state.forward?.toString().includes('&type=usim') || type === 'usim'
    ? 'usim'
    : 'esim'
const selectedTab = ref<'esim' | 'usim'>(defaultTab)

type GuideItem = {
  title: string
  content: string
  button: string
  imgLabel: string
  link: string
  external: boolean
}

const linkTo = (item: GuideItem) => {
  if (item.external) {
    return item.link
  } else {
    return partnersKey ? `/partners/${partnersKey}${item.link}` : item.link
  }
}

const esimGuides: GuideItem[] = [
  {
    title: '구매한 eSIM을 미리 설치해도 될까요?',
    content: '처음 사용하시는 분들은 구매 후 받은 카카오톡 또는 이메일을 확인해 보세요.',
    button: '설치 기준 확인하기',
    imgLabel: 'img-guide-banner-1-v3',
    link: '/guide/image?label=1',
    external: false
  },
  {
    title: '국내에서 설치해도 되나요?',
    content: '국내 설치가 가능한 상품은 국내에서 설치해 주세요.',
    button: '국내 설치 가이드 보기',
    imgLabel: 'img-guide-banner-2',
    link: '/guide/domestic',
    external: false
  },
  {
    title: '출국 전 설치하면 안되는 상품인가요?',
    content: '설치 즉시 개통되는 상품으로 반드시 여행지 도착 후 설치를 진행해 주세요',
    button: '현지 설치 가이드 보기',
    imgLabel: 'img-guide-banner-3',
    link: '/guide/local',
    external: false
  },
  {
    title: '데이터 사용에 문제가 있으신가요?',
    content:
      '데이터 속도 저하나 접속이 불가한 경우 또는 국가 이동 시 데이터 문제 시 확인해 주세요.',
    button: '오류 해결 가이드 보기',
    imgLabel: 'img-guide-banner-4-v2',
    link: '/guide/use?setting=error',
    external: false
  },
  {
    title: '여행이 끝나셨나요?',
    content: '여행이 끝난 후 eSIM 제거를 진행해야 하니 가능하실 때 진행해 주세요.',
    button: 'eSIM 삭제 가이드 보기',
    imgLabel: 'img-guide-banner-5-v2',
    link: '/guide/use?setting=delete',
    external: false
  },
  {
    title: '여행자 인증은 무엇인가요?',
    content: '특정국가에서 여행자 인증이 필요합니다. 구매한 상품 정보를 확인 후 진행해 주세요.',
    button: '여행자 인증 확인하기',
    imgLabel: 'img-guide-banner-6',
    link: 'https://usimsastart.notion.site/usimsa-traveler-verification-guide-kr',
    external: true
  },
  {
    title: '여행 중 데이터가 부족하신가요?',
    content: '속도 저하 시 데이터 충전, 여정의 기간이 도중에 길어졌다면 기간 연장을 해보세요!',
    button: '충전/연장 가이드 보기',
    imgLabel: 'img-guide-banner-7-v2',
    link: '/guide/image?label=2',
    external: false
  }
]

const usimGuides = [
  {
    title: 'USIM 사용 방법을 확인해 보세요.',
    content: 'USIM은 기존 유심과 구매하신 유심을 교체만 하면 사용이 가능합니다.',
    button: 'USIM 사용 방법 확인하기',
    imgLabel: 'img-guide-banner-8-v2',
    link: '/guide/image?label=3&type=usim',
    external: false
  },
  {
    title: '여행자 인증은 어떻게 진행하는 건가요?',
    content: '특정국가에서 여행자 인증이 필요합니다. 구매한 상품 정보를 확인 후 진행해 주세요.',
    button: '여행자 인증 확인하기',
    imgLabel: 'img-guide-banner-6',
    link: 'https://usimsastart.notion.site/usimsa-traveler-verification-guide-kr',
    external: true
  },
  {
    title: '여행 중 데이터가 부족하신가요?',
    content: '속도 저하 시 데이터 충전, 여정의 기간이 도중에 길어졌다면 기간 연장을 해보세요!',
    button: '충전/연장 가이드 보기',
    imgLabel: 'img-guide-banner-7-v2',
    link: '/guide/image?label=2&type=usim',
    external: false
  }
]
</script>

<style lang="scss" scoped>
.guide-tabs {
  padding-top: 54px;
  .guides {
    padding: 16px;
    .guide {
      margin-bottom: 24px;
      border: 1px $stroke-default solid;
      line-height: normal;
      cursor: pointer;
      @include rounded-lg;
      .texts {
        padding: 20px;
        .title {
          @include body3-semibold;
          color: $gray-700;
          margin-bottom: 4px;
          text-align: center;
        }
        .content {
          @include body5-regular;
          color: $gray-500;
          text-align: center;
          word-break: keep-all;
        }
        .button {
          color: $brand-point;
          @include body7-semibold;
        }
      }
      .guide-banner {
        aspect-ratio: 328/184;
        border-radius: 16px 16px 0 0;
        object-fit: cover;
      }
    }
  }
}
</style>
